import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";

import { useSelection } from "@/contexts/SelectionContext";
import { useSetSlideTags } from "@/lib/hooks/slides/useSetSlideTags";
import { useTags } from "@/lib/hooks/tags/useTags";

interface SetSlideTagsModalProps {
  isOpen: boolean;
}

export function SetSlideTagsModal({ isOpen }: SetSlideTagsModalProps) {
  const { selectedSlideIds, toggleSetTagsModal, clearAllSelections } = useSelection();
  const [tagIds, setTagIds] = useState<string[]>([]);

  const { setSlideTags, loading } = useSetSlideTags();
  const { tags } = useTags();

  const slideCount = selectedSlideIds.length;

  const toggleTag = (tagId: string) => {
    setTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleClose = () => {
    toggleSetTagsModal(false);
    clearAllSelections();
  };

  const handleConfirm = async () => {
    if (slideCount === 0 || tagIds.length === 0) return;
    try {
      await setSlideTags(selectedSlideIds, tagIds);
      handleClose();
    } catch (error) {
      // Error already surfaced by the hook's toast
      console.error("Set slide tags failed:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Set Tags for ${slideCount} Slide${slideCount === 1 ? "" : "s"}`}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="label flex items-center gap-1">
            <span className="text-red-500">*</span> Tag
          </label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => {
              const pickOrder = tagIds.indexOf(tag.id) + 1;
              const isSelected = pickOrder > 0;
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  style={{
                    borderColor: tag.color,
                    backgroundColor: isSelected ? tag.color : undefined,
                  }}
                  className={`h-9 px-4 rounded-full border label whitespace-nowrap transition-colors flex items-center gap-2 ${
                    isSelected
                      ? "text-white shadow-sm"
                      : "text-secondary-200 hover:bg-black/5 dark:hover:bg-white/5"
                  }`}
                >
                  {isSelected ? (
                    <span className="w-4 h-4 rounded-full bg-white/25 text-[10px] leading-none flex items-center justify-center">
                      {pickOrder}
                    </span>
                  ) : (
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                  )}
                  {tag.name}
                </button>
              );
            })}
          </div>
          {/* Pick order is stored order, and anything that needs a single tag takes the first. */}
          <p className="text-xs text-secondary-400">
            Replaces the existing tags. Tags decide which lesson section a slide appears under.
            Numbered by pick order — #1 is the one used where only one tag fits.
          </p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={slideCount === 0 || tagIds.length === 0 || loading}
          >
            {loading ? "Updating..." : "Set Tags"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
